import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import Confetti from './Confetti'; // Import Confetti component

const creditPhases = [
  { hex: '#DC2626', score: 450, status: 'Low' },
  { hex: '#E86A00', score: 550, status: 'Low' },
  { hex: '#F59E0B', score: 610, status: 'Improving' },
  { hex: '#FDE047', score: 670, status: 'Consistent' },
  { hex: '#A3E635', score: 730, status: 'Good', glow: true },
  { hex: '#22C55E', score: 790, status: 'Excellent', glow: true },
  { hex: '#8A2BE2', score: 800, status: '800+ Club', glow: true, confettiOnMax: true }
];
const habitPhases = [
  { hex: '#DC2626', number: 1, status: 'Poor' },
  { hex: '#E86A00', number: 2, status: 'Need Work' },
  { hex: '#F59E0B', number: 3, status: 'Developing' },
  { hex: '#FDE047', number: 4, status: 'Steady' },
  { hex: '#A3E635', number: 5, status: 'Strong', glow: true },
  { hex: '#22C55E', number: 6, status: 'Strategic', glow: true },
  { hex: '#8A2BE2', number: 7, status: 'Legendary', glow: true, confettiOnMax: true }
];

const thresholds = [0, 0.166, 0.333, 0.5, 0.666, 0.833, 1];

const lerp = (a, b, t) => a + (b - a) * t;

const hexToRgb = (hex) => {
  const m = hex.replace('#', '');
  return { r: parseInt(m.substring(0, 2), 16), g: parseInt(m.substring(2, 4), 16), b: parseInt(m.substring(4, 6), 16) };
};

const colorLerp = (c1, c2, t) => {
  const p1 = hexToRgb(c1);
  const p2 = hexToRgb(c2);
  const r = Math.round(lerp(p1.r, p2.r, t));
  const g = Math.round(lerp(p1.g, p2.g, t));
  const b = Math.round(lerp(p1.b, p2.b, t));
  return `rgb(${r}, ${g}, ${b})`;
};

const phaseAt = (phases, t) => {
  for (let i = 0; i < thresholds.length - 1; i++) {
    const t0 = thresholds[i];
    const t1 = thresholds[i + 1];
    if (t >= t0 && t <= t1) {
      const localT = (t - t0) / (t1 - t0);
      return { i0: i, i1: i + 1, localT };
    }
  }
  return { i0: thresholds.length - 2, i1: thresholds.length - 1, localT: 1 };
};

const TopBar = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [creditData, setCreditData] = useState(creditPhases[0]);
  const [habitData, setHabitData] = useState(habitPhases[0]);
  
  const [showCreditConfettiGood, setShowCreditConfettiGood] = useState(false);
  const [showHabitConfettiStrong, setShowHabitConfettiStrong] = useState(false);
  const [showCreditConfettiMax, setShowCreditConfettiMax] = useState(false);
  const [showHabitConfettiMax, setShowHabitConfettiMax] = useState(false);

  const creditRef = useRef(null);
  const habitRef = useRef(null);

  const creditGoodConfettiCount = useRef(0);
  const habitStrongConfettiCount = useRef(0);
  const creditMaxConfettiCount = useRef(0);
  const habitMaxConfettiCount = useRef(0);
  const MAX_CONFETTI_COUNT = 2; // Changed to 2

  const lastScrollY = useRef(0); // To track scroll direction

  const onScroll = useCallback(() => {
    const doc = document.documentElement;
    const max = (doc.scrollHeight - window.innerHeight) || 1;
    const t = Math.max(0, Math.min(1, window.scrollY / max));
    setScrollProgress(t);

    const currentScrollY = window.scrollY;
    const isScrollingDown = currentScrollY > lastScrollY.current;
    lastScrollY.current = currentScrollY;

    // Credit data
    const pc = phaseAt(creditPhases, t);
    const newCreditData = {
      hex: colorLerp(creditPhases[pc.i0].hex, creditPhases[pc.i1].hex, pc.localT),
      score: Math.round(lerp(creditPhases[pc.i0].score, creditPhases[pc.i1].score, pc.localT)),
      status: (pc.localT < 0.5) ? creditPhases[pc.i0].status : creditPhases[pc.i1].status,
      glow: (pc.localT < 0.5) ? creditPhases[pc.i0].glow : creditPhases[pc.i1].glow,
      confettiOnMax: (pc.localT < 0.5) ? creditPhases[pc.i0].confettiOnMax : creditPhases[pc.i1].confettiOnMax
    };
    setCreditData(newCreditData);

    // Habits data
    const ph = phaseAt(habitPhases, t);
    const newHabitData = {
      hex: colorLerp(habitPhases[ph.i0].hex, habitPhases[ph.i1].hex, ph.localT),
      number: Math.round(lerp(habitPhases[ph.i0].number, habitPhases[ph.i1].number, ph.localT)),
      status: (ph.localT < 0.5) ? habitPhases[ph.i0].status : habitPhases[ph.i1].status,
      glow: (ph.localT < 0.5) ? habitPhases[ph.i0].glow : habitPhases[ph.i1].glow,
      confettiOnMax: (ph.localT < 0.5) ? habitPhases[ph.i0].confettiOnMax : habitPhases[ph.i1].confettiOnMax
    };
    setHabitData(newHabitData);

    // Confetti logic for "Good" and "Strong"
    if (isScrollingDown) { // Only trigger on scroll down
      if (newCreditData.status === 'Good' && creditData.status !== 'Good' && creditGoodConfettiCount.current < MAX_CONFETTI_COUNT) {
        setShowCreditConfettiGood(true);
        creditGoodConfettiCount.current += 1;
      } else if (newCreditData.status !== 'Good' && creditData.status === 'Good') {
        setShowCreditConfettiGood(false);
      }

      if (newHabitData.status === 'Strong' && habitData.status !== 'Strong' && habitStrongConfettiCount.current < MAX_CONFETTI_COUNT) {
        setShowHabitConfettiStrong(true);
        habitStrongConfettiCount.current += 1;
      } else if (newHabitData.status !== 'Strong' && habitData.status === 'Strong') {
        setShowHabitConfettiStrong(false);
      }

      // Confetti logic for "800+ Club" and "Legendary" (max values)
      if (newCreditData.confettiOnMax && !creditData.confettiOnMax && creditMaxConfettiCount.current < MAX_CONFETTI_COUNT) {
        setShowCreditConfettiMax(true);
        creditMaxConfettiCount.current += 1;
      } else if (!newCreditData.confettiOnMax && creditData.confettiOnMax) {
        setShowCreditConfettiMax(false);
      }

      if (newHabitData.confettiOnMax && !habitData.confettiOnMax && habitMaxConfettiCount.current < MAX_CONFETTI_COUNT) {
        setShowHabitConfettiMax(true);
        habitMaxConfettiCount.current += 1;
      } else if (!newHabitData.confettiOnMax && habitData.confettiOnMax) {
        setShowHabitConfettiMax(false);
      }
    } else { // If scrolling up, ensure confetti flags are reset if conditions are no longer met
        if (newCreditData.status !== 'Good' && creditData.status === 'Good') {
            setShowCreditConfettiGood(false);
        }
        if (newHabitData.status !== 'Strong' && habitData.status === 'Strong') {
            setShowHabitConfettiStrong(false);
        }
        if (!newCreditData.confettiOnMax && creditData.confettiOnMax) {
            setShowCreditConfettiMax(false);
        }
        if (!newHabitData.confettiOnMax && habitData.confettiOnMax) {
            setShowHabitConfettiMax(false);
        }
    }

  }, [creditData, habitData]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onScroll);
    onScroll(); // Initial call

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [onScroll]);


  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Progressive color bars */}
      <div className="h-1 w-full bg-black">
        <div className="h-full" style={{ width: `${scrollProgress * 100}%`, backgroundColor: creditData.hex }} />
      </div>
      <div className="h-0.5 w-full opacity-95 bg-black">
        <div className="h-full" style={{ width: `${scrollProgress * 100}%`, backgroundColor: habitData.hex }} />
      </div>

      {/* Main black bar */}
      <div className="h-28 bg-black text-white flex items-center justify-between border-b border-gray-700/50 px-4 md:px-8 relative">
        {/* Financial Habits (Left) */}
        <div ref={habitRef} className="flex flex-col items-center text-center min-w-[120px] sm:min-w-[170px] md:min-w-[200px] pt-4">
          <label className="font-['Roboto_Condensed'] font-semibold uppercase tracking-wider text-gray-400 text-xs sm:text-sm leading-tight mt-1">
            YOUR FINANCIAL<br />HABITS
          </label>
          <motion.div
            className={`font-['Roboto_Mono'] font-medium text-xl sm:text-2xl md:text-3xl leading-none mt-0.5 md:mt-1.5 ${habitData.glow ? 'animate-pulse-slow' : ''}`}
            style={{ color: habitData.hex, textShadow: habitData.glow ? `0 0 10px ${habitData.hex}, 0 0 20px ${habitData.hex}` : `0 0 8px ${habitData.hex}, 0 0 12px ${habitData.hex}` }}
            key={habitData.number}
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {habitData.number}
          </motion.div>
          <motion.div
            className={`font-['Roboto_Condensed'] font-medium text-xs sm:text-sm uppercase tracking-wider -mt-0.5 md:-mt-1.5 ${habitData.glow ? 'animate-pulse-slow' : ''}`}
            style={{ color: habitData.hex }}
            key={habitData.status}
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {habitData.status}
          </motion.div>
        </div>

        {/* Logo (Center) - Adjusted for absolute centering and size */}
        <div className="absolute left-0 right-0 top-0 bottom-0 flex items-center justify-center pointer-events-none">
          <motion.img
            className="w-20 h-20 sm:w-24 sm:h-24 object-contain pointer-events-auto"
            src="https://res.cloudinary.com/dv1ald7mk/image/upload/v1754454293/DRB_lytp41.png"
            alt="DRB3 Logo"
            animate={{ rotateY: 360 }}
            transition={{ duration: 30, ease: "linear", repeat: Infinity }}
            style={{ marginTop: "-20px" }}
          />
        </div>

        {/* Credit Score (Right) */}
        <div ref={creditRef} className="flex flex-col items-center text-center min-w-[120px] sm:min-w-[170px] md:min-w-[200px] pt-4">
          <label className="font-['Roboto_Condensed'] font-semibold uppercase tracking-wider text-gray-400 text-xs sm:text-sm leading-tight mt-1">
            YOUR CREDIT<br />SCORE
          </label>
          <motion.div
            className={`font-['Roboto_Mono'] font-medium text-xl sm:text-2xl md:text-3xl leading-none mt-0.5 md:mt-1.5 ${creditData.glow ? 'animate-pulse-slow' : ''}`}
            style={{ color: creditData.hex, textShadow: creditData.glow ? `0 0 10px ${creditData.hex}, 0 0 20px ${creditData.hex}` : `0 0 8px ${creditData.hex}, 0 0 12px ${creditData.hex}` }}
            key={creditData.score}
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {creditData.score}
          </motion.div>
          <motion.div
            className={`font-['Roboto_Condensed'] font-medium text-xs sm:text-sm uppercase tracking-wider -mt-0.5 md:-mt-1.5 ${creditData.glow ? 'animate-pulse-slow' : ''}`}
            style={{ color: creditData.hex }}
            key={creditData.status}
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {creditData.status}
          </motion.div>
        </div>
      </div>
      {showCreditConfettiGood && creditRef.current && (
        <Confetti
          show={showCreditConfettiGood}
          originX={creditRef.current.getBoundingClientRect().left / window.innerWidth + (creditRef.current.offsetWidth / 2) / window.innerWidth}
          originY={creditRef.current.getBoundingClientRect().bottom / window.innerHeight}
          type="confetti"
        />
      )}
      {showHabitConfettiStrong && habitRef.current && (
        <Confetti
          show={showHabitConfettiStrong}
          originX={habitRef.current.getBoundingClientRect().left / window.innerWidth + (habitRef.current.offsetWidth / 2) / window.innerWidth}
          originY={habitRef.current.getBoundingClientRect().bottom / window.innerHeight}
          type="confetti"
        />
      )}
      {showCreditConfettiMax && creditRef.current && (
        <Confetti
          show={showCreditConfettiMax}
          originX={creditRef.current.getBoundingClientRect().left / window.innerWidth + (creditRef.current.offsetWidth / 2) / window.innerWidth}
          originY={creditRef.current.getBoundingClientRect().bottom / window.innerHeight}
          type="confetti"
        />
      )}
      {showHabitConfettiMax && habitRef.current && (
        <Confetti
          show={showHabitConfettiMax}
          originX={habitRef.current.getBoundingClientRect().left / window.innerWidth + (habitRef.current.offsetWidth / 2) / window.innerWidth}
          originY={habitRef.current.getBoundingClientRect().bottom / window.innerHeight}
          type="confetti"
        />
      )}
    </div>
  );
};

export default TopBar;